import React, { useMemo, useRef, useState } from "react";

import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { MaterialReactTable, MRT_ColumnDef, MRT_ColumnOrderState, MRT_Row, useMaterialReactTable } from "material-react-table";

import { SmartSheetFlatRow } from "./SmartSheetTable";

export interface TableProps {
  data?: SmartSheetFlatRow[];
  columns?: MRT_ColumnDef<SmartSheetFlatRow>[];
  isLoading?: boolean;
  onFetchNextPage?: () => void;
  enableRowSelection?: boolean | ((row: MRT_Row<SmartSheetFlatRow>) => boolean);
  getRowId?: (row: SmartSheetFlatRow, index: number) => string;
  rowPinning?: { top: string[] };
  onColumnOrderChange?: (updater: React.SetStateAction<MRT_ColumnOrderState>) => void;
  lockedFirstColumnId?: string;
}

export const Table: React.FC<TableProps> = ({
  data,
  columns,
  isLoading = false,
  onFetchNextPage,
  enableRowSelection = true,
  getRowId,
  rowPinning,
  onColumnOrderChange,
  lockedFirstColumnId
}) => {
  const handleContainerScroll = useMemo(
    () =>
      onFetchNextPage
        ? (e: React.UIEvent<HTMLDivElement>) => {
            const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
            if (scrollHeight <= clientHeight) return;

            if (scrollHeight - scrollTop - clientHeight < 300) {
              onFetchNextPage();
            }
          }
        : undefined,
    [onFetchNextPage]
  );

  const columnKeys = (columns ?? []).map((col) => col.id ?? (col.accessorKey as string)).filter(Boolean);

  const [mrtColumnOrder, setMrtColumnOrder] = useState<string[]>(columnKeys);

  // isDraggingRef prevents overriding MRT order while user is dragging
  const isDraggingRef = useRef(false);

  // Sync MRT order from props whenever the incoming order differs —
  // but only when user is not actively dragging
  const columnOrderSignature = JSON.stringify(columnKeys);
  const prevColumnOrderSignatureRef = useRef<string>(columnOrderSignature);

  if (!isDraggingRef.current && prevColumnOrderSignatureRef.current !== columnOrderSignature) {
    prevColumnOrderSignatureRef.current = columnOrderSignature;
    setMrtColumnOrder(columnKeys);
  }

  const handleColumnOrderChangeInternal = (updater: React.SetStateAction<MRT_ColumnOrderState>) => {
    setMrtColumnOrder((prev) => {
      let next = typeof updater === "function" ? updater(prev) : updater;

      // Enforce locked first column — move it back to front if anything displaced it
      if (lockedFirstColumnId) {
        const idx = next.indexOf(lockedFirstColumnId);
        if (idx > 0) {
          next = [lockedFirstColumnId, ...next.filter((k) => k !== lockedFirstColumnId)];
        }
      }

      onColumnOrderChange?.(next);
      return next;
    });
  };

  const table = useMaterialReactTable<SmartSheetFlatRow>({
    columns: columns ?? [],
    data: data ?? [],
    layoutMode: "grid",
    enableColumnPinning: true,
    getRowId,
    state: {
      isLoading,
      columnOrder: mrtColumnOrder,
      columnPinning: lockedFirstColumnId ? { left: [lockedFirstColumnId] } : {},
      ...(rowPinning && { rowPinning, columnVisibility: { "mrt-row-pin": false } })
    },
    enableRowPinning: !!rowPinning,
    rowPinningDisplayMode: "top",
    mrtTheme: { selectedRowBackgroundColor: "#ffffff", pinnedRowBackgroundColor: "#ffffff" },
    enableColumnActions: false,
    enableColumnFilters: false,
    enableColumnResizing: true,
    enableColumnOrdering: !!onColumnOrderChange,
    onColumnOrderChange: onColumnOrderChange ? handleColumnOrderChangeInternal : undefined,
    muiColumnDragHandleProps: {
      onDragStart: () => { isDraggingRef.current = true; },
      onDragEnd: () => { isDraggingRef.current = false; },
      title: ""
    },
    icons: { DragHandleIcon: () => <DragIndicatorIcon sx={{ fontSize: 16, color: "#92a7bc" }} /> },
    enablePagination: false,
    enableSorting: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableRowSelection,
    enableStickyHeader: true,
    enableRowVirtualization: true,
    muiSelectAllCheckboxProps: {
      size: "small",
      sx: { p: 0 }
    },
    muiSelectCheckboxProps: {
      size: "small",
      sx: { p: 0 }
    },
    displayColumnDefOptions: {
      "mrt-row-select": {
        enableResizing: false,
        size: 48,
        minSize: 48,
        maxSize: 48,
        muiTableHeadCellProps: {
          sx: {
            py: "4px",
            px: "8px",
            borderBottom: "1px solid #dbe8f5"
          }
        },
        muiTableBodyCellProps: {
          sx: {
            py: "4px",
            px: "8px",
            borderBottom: "1px solid #dbe8f5"
          }
        }
      }
    },
    columnResizeMode: "onChange",
    muiTablePaperProps: {
      sx: {
        boxShadow: "none",
        border: "1px solid #dbe8f5",
        borderRadius: "8px",
        overflow: "hidden",
        height: "100%",
        "& thead th .MuiIconButton-root": { opacity: "0 !important", transition: "opacity 0.15s" },
        "& thead th:hover .MuiIconButton-root": { opacity: "0.7 !important" },
        "& thead th .MuiTableSortLabel-root": { opacity: "0 !important", transition: "opacity 0.15s" },
        "& thead th:hover .MuiTableSortLabel-root": { opacity: "1 !important" },
        "& thead th[data-sort] .MuiTableSortLabel-root": { opacity: "1 !important" },
        "& tr[data-pinned='true'] td": { borderBottom: "1px solid #dbe8f5" },
        // Remove MRT default box-shadow on pinned columns
        "& th[data-pinned='true']::after, & td[data-pinned='true']::after": { boxShadow: "none !important" },
        "& th[data-pinned='true'], & td[data-pinned='true']": { boxShadow: "2px 0 4px -2px rgba(0,0,0,0.08)" }
      }
    },
    muiTableProps: {
      sx: { paddingBottom: "15px" }
    },
    muiTableContainerProps: {
      sx: { maxHeight: "100%", backgroundColor: "white" },
      onScroll: handleContainerScroll
    },
    muiTableHeadCellProps: {
      sx: {
        backgroundColor: "#f6f8fc",
        color: "#071f35",
        fontSize: 14,
        fontWeight: 500,
        height: 40,
        minHeight: 40,
        maxHeight: 40,
        py: "4px",
        px: "16px",
        borderBottom: "1px solid #dbe8f5",
        overflow: "visible",
        "& .Mui-TableHeadCell-Content-Labels": {
          flex: 1,
          minWidth: 0,
          overflow: "hidden"
        },
        "& .Mui-TableHeadCell-Content-Wrapper": {
          width: "100%",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          "&:hover": { textOverflow: "ellipsis" }
        },
        "& .MuiDivider-root:not(.Mui-TableHeadCell-ResizeHandle-Divider)": { display: "none" },
        "& .Mui-TableHeadCell-ResizeHandle-Wrapper": {
          cursor: "col-resize",
          position: "absolute",
          paddingLeft: "0px",
          right: "0px !important",
          borderColor: "#e6e8f2 !important"
        },
        "& .Mui-TableHeadCell-ResizeHandle-Divider": {
          borderColor: "#e6e8f2 !important",
          opacity: 1,
          borderWidth: "1px !important"
        },
        "&::before": { display: "none" }
      }
    },
    muiTableHeadRowProps: {
      sx: {
        boxShadow: "none",
        height: "40px",
        minHeight: "40px",
        "& th": { height: "40px", minHeight: "40px" }
      }
    },
    muiTableBodyCellProps: {
      sx: {
        backgroundColor: "white",
        fontSize: 14,
        color: "#071f35",
        borderBottom: "1px solid #dbe8f5",
        height: 48,
        minHeight: 48,
        maxHeight: 48,
        py: "4px",
        px: "8px",
        verticalAlign: "middle",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        overflow: "hidden",
        "tr:hover &": { backgroundColor: "#f7f8fa !important" },
        "tr:has([aria-expanded='true']) &": { backgroundColor: "#f7f8fa !important" },
        "&.Mui-selected": { backgroundColor: "white !important" },
        "&::before": { display: "none" }
      }
    },
    muiTableBodyRowProps: ({ row }) => ({
      hover: row.index !== 0,
      sx: {
        backgroundColor: "white",
        height: "48px",
        minHeight: "48px",
        cursor: "default",
        "& td": { verticalAlign: "middle", height: "48px", minHeight: "48px" },
        "&:hover": row.index === 0 ? {} : { backgroundColor: "#ecf1f8 !important" },
        "&:hover td": row.index === 0 ? {} : { backgroundColor: "#ecf1f8 !important" },
        "&:hover td::after": row.index === 0 ? {} : { backgroundColor: "#ecf1f8" },
        "&:hover .row-hover-actions": row.index === 0 ? {} : { opacity: 1 },
        "&:hover .pinned-actions-cell": row.index === 0 ? {} : { opacity: 1, pointerEvents: "auto" as const },
        "&:has([aria-expanded='true'])": { backgroundColor: "#ecf1f8 !important" },
        "&:has([aria-expanded='true']) td": { backgroundColor: "#ecf1f8 !important" },
        "&:has([aria-expanded='true']) td::after": { backgroundColor: "#ecf1f8" },
        "&:has([aria-expanded='true']) .row-hover-actions": { opacity: 1 },
        "&:has([aria-expanded='true']) .pinned-actions-cell": { opacity: 1, pointerEvents: "auto" as const },
        "&:last-child td": { borderBottom: "none" },
        "&.Mui-selected td": { backgroundColor: "white !important" }
      }
    })
  });

  return <MaterialReactTable table={table} />;
};
